import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {SYMBOL} from "../enums/Enum";
import {StickyScatter} from "../components/StickyScatter";
import {StickyDeadMultiplier} from "../components/StickyDeadMultiplier";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {GameUtils} from "../utils/GameUtils";
import {Game} from "../Game";
import {IStickyDeadSymInfo} from "../server/Data";
import {SymbolContainer} from "../reels/SymbolContainer";
import {Config} from "../config/Config";
import {Symbol} from "../reels/Symbol";
import {Sound} from "../sound/Sound";
import {DisplayObject} from "pixi.js";

export class StickyView extends PIXI.Container implements StageLayer {
    private _scatters: Map<number, StickyScatter> = new Map<number, StickyScatter>();
    private _deadMultiplierMap: Map<SYMBOL, StickyDeadMultiplier> = new Map<SYMBOL, StickyDeadMultiplier>();
    private _deadMultiplierContainer: PIXI.Container;
    private _scatterContainer: PIXI.Container;
    private readonly X_GAP: number = 100;
    private readonly Y_GAP: number = 95;
    private readonly PORTRAIT_INITIAL_GAP: number = 660;
    private _usedMap: Map<string, [{ reelId: number, rowId: number, subRowId: number }]> = new Map<string, [{
        reelId: number;
        rowId: number;
        subRowId: number
    }]>();

    constructor() {
        super();
        this.name = "StickyView";
        this.init();
    }

    public onRestore(): void {
        for (let symName in Game.model.data.stickyDeadSymCounterNextSpin) {
            const deadSymInfo: IStickyDeadSymInfo = Game.model.data.stickyDeadSymCounterNextSpin[symName];
            if (deadSymInfo) {
                const stickyDeadMultiplier: StickyDeadMultiplier = this.createStickyDeadMul(symName as SYMBOL);
                stickyDeadMultiplier.alpha = 1;
                stickyDeadMultiplier.updateMultiplier(deadSymInfo.multi, deadSymInfo.savedCount)
            }
        }
        this.reArrangeStickDeadMultiplier(0);
    }

    onResize() {
        if (NolimitApplication.isLandscape) {
            this._deadMultiplierContainer.position.set(790, 150);
            this._deadMultiplierContainer.children.forEach((child: PIXI.DisplayObject, index: number) => {
                child.position.x = 0;
                child.position.y = index * this.Y_GAP;
            });
        } else {
            this._deadMultiplierContainer.position.set(0, -30);
            this._deadMultiplierContainer.children.forEach((child: PIXI.DisplayObject, index: number) => {
                child.position.y = 0;
                child.position.x = this.PORTRAIT_INITIAL_GAP - ((this._deadMultiplierContainer.children.length - 1 - index) * this.X_GAP);
            });
        }
    }

    public moveFireCircle(sourcePos: PIXI.Point, destPos: PIXI.Point): gsap.core.Timeline {

        const tl: gsap.core.Timeline = gsap.timeline();
        const fireBallTLs: PIXI.Sprite = this.createMovingShine(sourcePos);
        const blastTlsTo: TimelineSprite = this.createBlastTimelineSprite();
        blastTlsTo.position.set(destPos.x, destPos.y);
        this.addChild(blastTlsTo);
        const angle: number = GameUtils.getAngleInDegree([sourcePos.x, sourcePos.y], [destPos.x, destPos.y]);
        fireBallTLs.angle = angle - 90;
        tl.add(() => {
            this.addChild(fireBallTLs);
            fireBallTLs.visible = true;
        });
        tl.to(fireBallTLs, {alpha: 1, duration: 0.001});
        tl.to(fireBallTLs, {x: destPos.x, y: destPos.y, duration: 0.25});
        tl.add([
            gsap.to(fireBallTLs.scale, {x: 0, y: 0, duration: 0.1}),
            blastTlsTo.getAnimationAutoShowHide(true, true)
        ], 0.21);

        tl.add(() => {
            blastTlsTo?.parent.removeChild(blastTlsTo);
            fireBallTLs.parent.removeChild(fireBallTLs);
        });
        return tl;
    }

    public updateStickyDeadMultiplier(sourcePos: PIXI.Point, symName: SYMBOL): gsap.core.Timeline {
        const deadSymInfo: IStickyDeadSymInfo = Game.model.gameData.stickyDeadSymCounterNextSpin[symName];
        const tl: gsap.core.Timeline = gsap.timeline();
        if (deadSymInfo) {
            let stickyDeadMultiplier: StickyDeadMultiplier | undefined = this._deadMultiplierMap.get(symName);
            if (!stickyDeadMultiplier) {
                stickyDeadMultiplier = this.createStickyDeadMul(symName);
            }
            const destPos: PIXI.Point = new PIXI.Point(this._deadMultiplierContainer.x + stickyDeadMultiplier.x, this._deadMultiplierContainer.y + stickyDeadMultiplier.y);
            tl.to(stickyDeadMultiplier, {alpha: 1, duration: 0.001});
            tl.add(() => {
                Game.soundPlayer.avoidFrequentPlaySoundAndPlayLastQueued(Sound.DEAD_MULTI_TO_STICKY, Sound.DEAD_MULTI_TO_STICKY);
            })
            tl.add(this.moveFireCircle(sourcePos, destPos));
            tl.add(() => {
                stickyDeadMultiplier?.updateMultiplier(deadSymInfo.multi, deadSymInfo.savedCount);
            }, 0.23);
        }
        return tl;
    }

    public addScatters(reelId: number, rowId: number, sym: SYMBOL): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const key: number = reelId * 3 + rowId;
        let stickyScatter: StickyScatter | undefined = this._scatters.get(key);
        if (!stickyScatter) {
            stickyScatter = new StickyScatter(reelId, rowId);
            this._scatterContainer.addChild(stickyScatter);
            this._scatters.set(key, stickyScatter);
        }
        stickyScatter.isFirstScatter = this._scatterContainer.children.indexOf(stickyScatter) === 0;
        tl.add(stickyScatter.showHide(true));
        return tl;
    }

    public hideScatters(reelId: number, rowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const key: number = reelId * 3 + rowId;
        const stickyScatter: StickyScatter | undefined = this._scatters.get(key);
        if (stickyScatter) {
            tl.add(stickyScatter.showHide(false));
        }
        return tl;
    }

    public checkAndSetStickyDeadMultiplierToSymbol(symbolContainer: SymbolContainer): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        symbolContainer.symbols.forEach((symbol, subRowId: number) => {
            Game.model.deadMultiplierPlacedPositions[symbolContainer.reelId][symbolContainer.rowId][subRowId] = symbol.getMultiplier() > 0;
            if (Game.model.stickyDeadSymInfoCopy[symbol.symbolName]) {
                tl.add(this.sendMultiplierToSpecificSymbol(symbolContainer.reelId, symbolContainer.rowId, subRowId, symbol.symbolName), 0)
            }
        })
        return tl;
    }

    public sendMultiplierToSpecificSymbol(reelId: number, rowId: number, subRowId: number, eligibleSymName?: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (Game.model.isStickyDeadApplicable() && !Game.model.deadMultiplierPlacedPositions[reelId][rowId][subRowId]) {
            const symContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
            if (symContainer.symbols.length > subRowId) {
                const symbol: Symbol = symContainer.symbols[subRowId];
                const multSymName: string = eligibleSymName || symbol.symbolName;
                const deadSymInfo: IStickyDeadSymInfo = Game.model.stickyDeadSymInfoCopy[multSymName];

                const stickyDeadMultiplier: StickyDeadMultiplier | undefined = this._deadMultiplierMap.get(multSymName as SYMBOL);
                if (deadSymInfo?.used > 0 && stickyDeadMultiplier) {
                    Game.model.deadMultiplierPlacedPositions[reelId][rowId][subRowId] = true;
                    if (this._usedMap.get(multSymName)) {
                        this._usedMap.get(multSymName)?.push({reelId: reelId, rowId: rowId, subRowId: subRowId});
                    } else {
                        this._usedMap.set(multSymName, [{reelId: reelId, rowId: rowId, subRowId: subRowId}]);
                    }
                    deadSymInfo.used--;
                    const destPos: PIXI.Point = GameUtils.getSymbolPos(reelId, rowId);
                    if (reelId === 2 && rowId === 0) { //mid reel top half symbol handling
                        destPos.y = destPos.y - 49;
                    }
                    const deadMulPos: PIXI.Point = new PIXI.Point(Config.SYMBOL_WIDTH * 0.5, symbol.currentHeight * 0.5 - 10);
                    destPos.x += symbol.position.x + deadMulPos.x - 30;
                    destPos.y += symbol.position.y + deadMulPos.y - 10 - symContainer.pivot.y;
                    const sourcePos: PIXI.Point = new PIXI.Point(this._deadMultiplierContainer.x + stickyDeadMultiplier.x, this._deadMultiplierContainer.y + stickyDeadMultiplier.y);
                    if (Game.model.isCFS()) {
                        tl.add(this.setStickyCount(stickyDeadMultiplier));
                    }
                    tl.add(() => {
                        Game.soundPlayer.avoidFrequentPlaySoundAndPlayLastQueued(Sound.STICKY_MULTI_TO_SYMBOL, Sound.STICKY_MULTI_TO_SYMBOL);
                    });
                    tl.add(this.moveFireCircle(sourcePos, destPos), 0);
                    tl.add(() => {
                        symbol.addDeadMultiplier(deadSymInfo.multi);
                    }, 0.22);
                }
            }
        }
        return tl;
    }

    public sendMultiplierToLandedReel(reelId: number, rowId: number, subRowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (Game.model.isStickyDeadApplicable() && !Game.model.deadMultiplierPlacedPositions[reelId][rowId][subRowId]) {
            const symContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
            if (symContainer.symbols.length > subRowId) {
                const symbol: Symbol = symContainer.symbols[subRowId];
                const deadSymInfo: IStickyDeadSymInfo = Game.model.stickyDeadSymInfoCopy[symbol.symbolName];
                const stickyDeadMultiplier: StickyDeadMultiplier | undefined = this._deadMultiplierMap.get(symbol.symbolName as SYMBOL);
                if (stickyDeadMultiplier) {
                    Game.model.deadMultiplierPlacedPositions[reelId][rowId][subRowId] = true;
                    if (this._usedMap.get(symbol.symbolName)) {
                        this._usedMap.get(symbol.symbolName)?.push({reelId: reelId, rowId: rowId, subRowId: subRowId});
                    } else {
                        this._usedMap.set(symbol.symbolName, [{reelId: reelId, rowId: rowId, subRowId: subRowId}]);
                    }
                    const destPos: PIXI.Point = GameUtils.getSymbolPos(reelId, rowId);
                    if (reelId === 2 && rowId === 0) { //mid reel top half symbol handling
                        destPos.y = destPos.y - 49;
                    }
                    const deadMulPos: PIXI.Point = new PIXI.Point(Config.SYMBOL_WIDTH * 0.5, symbol.currentHeight * 0.5 - 10);
                    destPos.x += symbol.position.x + deadMulPos.x - 30;
                    destPos.y += symbol.position.y + deadMulPos.y - 10;
                    const sourcePos: PIXI.Point = new PIXI.Point(this._deadMultiplierContainer.x + stickyDeadMultiplier.x, this._deadMultiplierContainer.y + stickyDeadMultiplier.y);
                    if (Game.model.isCFS()) {
                        tl.add(this.setStickyCount(stickyDeadMultiplier));
                    }
                    tl.add(() => {
                        Game.soundPlayer.avoidFrequentPlaySoundAndPlayLastQueued(Sound.STICKY_MULTI_TO_SYMBOL, Sound.STICKY_MULTI_TO_SYMBOL);
                    })
                    tl.add(this.moveFireCircle(sourcePos, destPos), 0);
                    tl.add(() => {
                        symbol.addDeadMultiplier(deadSymInfo.multi);
                    }, 0.22);
                }
            }
        }

        return tl;
    }

    public distributeDeadMultiplierToReel(reelId: number): gsap.core.Timeline {
        if (reelId === 0) {
            this._usedMap.clear();
        }
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        Game.model.deadMultiplierToBePlacedPositionForReels[reelId].forEach((rows: boolean[], rowId: number) => {
            rows.forEach((isTrue: boolean, subRowId: number) => {
                if (isTrue) {
                    tl.add(this.sendMultiplierToLandedReel(reelId, rowId, subRowId), 0);
                }
            });
        });
        return tl;
    }

    public removeStickyStuffs(): void {
        this._scatters.forEach((stickyScatter: StickyScatter) => {
            stickyScatter.destroy();
        });
        this._deadMultiplierMap.forEach((stickyDeadMultiplier: StickyDeadMultiplier) => {
            stickyDeadMultiplier.stopBGFire();
            stickyDeadMultiplier.destroy();
        });
        this._scatters.clear();
        this._deadMultiplierMap.clear();
        this._deadMultiplierContainer.removeChildren();
        this._scatterContainer.removeChildren();
    }

    public reArrangeStickDeadMultiplier(duration: number = 0.1): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const symMultArr: string[] = [];
        for (let sym in Game.model.data.stickyDeadSymCounterNextSpin) {
            symMultArr.push(Game.model.data.stickyDeadSymCounterNextSpin[sym].multi + "_" + sym);
        }
        symMultArr.sort((a: string, b: string) => {
            const firstOperand = a.split("_");
            const secondOperand = b.split("_");
            const mult1st: number = +firstOperand[0] + (5 - +(firstOperand[1].substring(1)));
            const mult2nd: number = +secondOperand[0] + (5 - +(secondOperand[1].substring(1)));
            return mult2nd - mult1st;
        });
        symMultArr.forEach((symMult: string, index: number) => {
            const sym: SYMBOL = symMult.split("_")[1] as SYMBOL;
            const multObj: StickyDeadMultiplier | undefined = this._deadMultiplierMap.get(sym);
            if (multObj) {
                this._deadMultiplierContainer.addChildAt(multObj, index);
                if (NolimitApplication.isLandscape) {
                    tl.to(multObj, {x: 0, y: this.Y_GAP * index, duration: duration}, 0);
                } else {
                    tl.to(multObj, {
                        x: this.PORTRAIT_INITIAL_GAP - ((this._deadMultiplierContainer.children.length - 1 - index) * this.X_GAP),
                        y: 0,
                        duration: duration
                    }, 0);
                }
            }
        });
        return tl;
    }

    private setStickyCount(stickyDeadMultiplier: StickyDeadMultiplier): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        stickyDeadMultiplier.count--;
        tl.add(() => {
            if (stickyDeadMultiplier.count <= 0) {
                this._deadMultiplierMap.delete(stickyDeadMultiplier.name as SYMBOL);
            }
        });
        if (stickyDeadMultiplier.count <= 0) {
            tl.add(this.fillEmptySpace(), 0.1);
        }
        return tl;
    }

    private fillEmptySpace(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        let index: number = 0;
        this._deadMultiplierContainer.children.forEach((multObj: DisplayObject, childIndex: number) => {
            if (!(multObj as StickyDeadMultiplier).isRemoved) {
                if (NolimitApplication.isLandscape) {
                    tl.to(multObj, {x: 0, y: this.Y_GAP * index, duration: 0.1}, 0);
                } else {
                    tl.to(multObj, {
                        x: this.PORTRAIT_INITIAL_GAP - ((this._deadMultiplierContainer.children.length - 1 - index) * this.X_GAP),
                        y: 0,
                        duration: 0.1
                    }, 0);
                }
                index++;
            }
        });
        return tl;
    }

    private createMovingShine(pos: PIXI.Point): PIXI.Sprite {
        const iconShine: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures("shine/shine")[0]);
        iconShine.visible = false;
        iconShine.alpha = 0;
        iconShine.anchor.set(0.5);
        iconShine.scale.set(3);
        iconShine.position = pos;
        iconShine.blendMode = PIXI.BLEND_MODES.ADD;
        return iconShine;
    }

    private createBlastTimelineSprite(): TimelineSprite {
        const item: TimelineSprite = new TimelineSprite(Game.assetLoader.getTextures("xhole_win_blast/xhole_win_blast_000"));
        item.hide();
        item.anchor.set(0.5);
        item.scale.set(0.5);
        item.blendMode = PIXI.BLEND_MODES.ADD;
        return item;
    }

    private createStickyDeadMul(symName: SYMBOL): StickyDeadMultiplier {
        const stickyDeadMultiplier: StickyDeadMultiplier = new StickyDeadMultiplier(symName);
        this._deadMultiplierMap.set(symName, stickyDeadMultiplier);
        if (NolimitApplication.isLandscape) {
            stickyDeadMultiplier.position.y = this.Y_GAP * this._deadMultiplierContainer.children.length;
        } else {
            stickyDeadMultiplier.position.x = this.PORTRAIT_INITIAL_GAP - ((this._deadMultiplierContainer.children.length) * this.X_GAP);
        }
        stickyDeadMultiplier.alpha = 0;
        this._deadMultiplierContainer.addChild(stickyDeadMultiplier);
        return stickyDeadMultiplier;
    }

    private init() {
        this.position.set(0, Config.MARGIN_TOP);
        this._scatterContainer = new PIXI.Container();
        this._scatterContainer.name = "ScatterContainer";
        this._deadMultiplierContainer = new PIXI.Container();
        this._deadMultiplierContainer.name = "DeadMultiplierContainer";
        this.addChild(this._deadMultiplierContainer, this._scatterContainer);
        this.onResize();
    }

}